import { isValidIsoDate } from './iso-date';

// Budget reset periods — how often a budget's hours allowance resets, stored
// on `customer_budgets.reset_period`. Canonical home so the API
// (schema/validators/import), the typed clients, and the web UI all derive from
// one source and can never drift.
//
// Stored values are stable lowercase codes; the human-facing label lives
// alongside in BUDGET_RESET_PERIOD_LABELS. The vocabulary deliberately mirrors the
// SOW retainer frequencies (`sows/schema.ts` FREQUENCIES) — same four buckets,
// same codes — so a reader who knows one doesn't have to learn the other.
// They are NOT imported from each other: a SOW frequency and a budget period
// are independent concepts that happen to share a vocabulary today, and
// coupling them would make either one impossible to extend alone.

export const BUDGET_RESET_PERIODS = ['monthly', 'quarterly', 'semi_annual', 'annual'] as const;
export type BudgetResetPeriod = (typeof BUDGET_RESET_PERIODS)[number];

export const BUDGET_RESET_PERIOD_LABELS: Record<BudgetResetPeriod, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
};

/** Length of one reset period in calendar months. Drives the window maths below. */
export const BUDGET_RESET_PERIOD_MONTHS: Record<BudgetResetPeriod, number> = {
  monthly: 1,
  quarterly: 3,
  semi_annual: 6,
  annual: 12,
};

/**
 * The timesheet statuses whose hours CONSUME a budget pool.
 *
 * Canonical home, for the reason BUDGET_RESET_PERIOD_MONTHS has one: "which
 * timesheet hours count as real work" is asked in several places that must
 * agree, and until this existed nothing made them. Keep this list current —
 * it is what a reader consults before widening the set, and (5) means the
 * blast radius now reaches the customer.
 *
 *  1. `analytics/sql/timesheet-facts.sql` — the shared facts SELECT's
 *     `budget_consuming_hours` column, which the Budget vs Actuals report sums.
 *  2. `analytics/sql/budget-actuals.sql` — reads (1) and states no predicate of
 *     its own any more.
 *  3. `findHoursForCustomerRoleInWindow` in the timesheets repository, behind
 *     the entry/approval over-budget signal.
 *  4. The customer Budgets tab's per-period actuals, which read (1) through
 *     `BudgetPoolQuery` — the same statement as the report.
 *
 * NOT `SUMMARIZABLE_STATUSES` (client-facing summaries). That set used to be
 * this one by reference; it is its own list now, because the two questions
 * came apart when this set took in rejected and failed hours (below). A
 * customer must never be quoted work an approver disallowed, whereas a budget
 * has to see hours that were logged against it whatever their approval state.
 *
 * (1) and (2) are now ONE relation. (3) is Drizzle over `timesheets` and cannot
 * read a `.sql` file, so it imports this instead — and a test in
 * `analytics/model.test.ts` pins the SQL to this same table, exactly as it pins
 * the reset-period spans. Add a status here and that test fails until the facts
 * SELECT learns it, rather than the report and the warning quietly disagreeing
 * about whether an account is over.
 *
 * EVERY STATUS BUT DRAFT. Counting only the approved half would be the
 * defensible finance number and is silent about work that is done and waiting:
 * a pool reads healthy right up until approvals catch up, which is precisely
 * when the warning is worth having. Rejected and failed hours count too (user
 * request, 2026-10-05): the work was done and logged against the account, and
 * a rejection is usually about the entry's details, not about whether the
 * hours happened — leaving them out made a pool look healthier than it was
 * while those entries waited to be corrected and resubmitted. Correcting and
 * resubmitting moves the SAME row back to `submitted`, so that path counts
 * once. The cost, accepted: a rejected row keeps counting until it is
 * resubmitted or DELETED — an entry rejected as a duplicate, or replaced by a
 * new corrected entry, double-counts while it lingers, and the approval engine
 * also lands entries `rejected` when no policy matches. A draft has nobody
 * standing behind it yet, so it still does not count.
 *
 * These are the STORED lowercase values. The reporting layer also carries an
 * `INITCAP`ed form for its `status` DIMENSION — see `initcap` in
 * `analytics/model.ts` — and the two are not interchangeable.
 */
export const BUDGET_CONSUMING_STATUSES = ['submitted', 'approved', 'rejected', 'failed'] as const;
export type BudgetConsumingStatus = (typeof BUDGET_CONSUMING_STATUSES)[number];

export function isBudgetResetPeriod(v: unknown): v is BudgetResetPeriod {
  return typeof v === 'string' && (BUDGET_RESET_PERIODS as readonly string[]).includes(v);
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Both window helpers below index a lookup by `resetPeriod` and slice the year
 * and month out of `isoDate`. Neither operation fails loudly on bad input: an
 * unknown period yields `undefined` months and a NaN start month, a malformed
 * date yields a NaN year, and the result is a plausible-looking
 * `'2026-NaN-01'` that flows into date comparisons and files hours against a
 * period that doesn't exist. `$type<BudgetResetPeriod>()` on the column is a
 * cast, not a guarantee — a row written by a migration, an import, or a
 * relaxed CHECK can carry anything. Fail loudly instead (G15).
 */
function assertWindowInput(resetPeriod: BudgetResetPeriod, isoDate: string): void {
  if (!isBudgetResetPeriod(resetPeriod)) {
    throw new Error(`Unknown budget reset period: ${JSON.stringify(resetPeriod)}`);
  }
  if (!isValidIsoDate(isoDate)) {
    throw new Error(`Invalid date for budget period window: ${JSON.stringify(isoDate)}`);
  }
}

/**
 * The calendar period containing `isoDate`, as a half-open `[start, end)`
 * range of `YYYY-MM-DD` strings.
 *
 * Periods are anchored to the CALENDAR year, not to the budget's
 * `effectiveFrom` and not to a fiscal year: quarters are Jan–Mar / Apr–Jun /
 * Jul–Sep / Oct–Dec, halves are Jan–Jun / Jul–Dec, annual is Jan–Dec. This is
 * the rule a finance team reads off a calendar without being told, and it
 * makes two budgets on the same customer comparable even when they were
 * created months apart.
 *
 * A tenant on a non-calendar fiscal year would need an anchor month on the
 * tenant (or the budget) and an offset here — see docs/budgets.md.
 *
 * All arithmetic is on the date STRING, never a `Date`: `new Date('2026-07-01')`
 * parses as UTC midnight and then reports July *June* 30th to anyone west of
 * Greenwich, which would put an entry in the wrong period for a third of the
 * planet.
 */
export function budgetPeriodWindow(
  resetPeriod: BudgetResetPeriod,
  isoDate: string,
): { start: string; end: string } {
  assertWindowInput(resetPeriod, isoDate);
  const year = Number(isoDate.slice(0, 4));
  const month = Number(isoDate.slice(5, 7)); // 1-12
  const span = BUDGET_RESET_PERIOD_MONTHS[resetPeriod];
  // Index of the period within the year, then back to a 1-based start month.
  const startMonth = Math.floor((month - 1) / span) * span + 1;
  const endMonthExclusive = startMonth + span;
  // Rolls into January of the next year when the period ends in December.
  const endYear = endMonthExclusive > 12 ? year + 1 : year;
  const endMonth = endMonthExclusive > 12 ? endMonthExclusive - 12 : endMonthExclusive;
  return {
    start: `${year}-${pad2(startMonth)}-01`,
    end: `${endYear}-${pad2(endMonth)}-01`,
  };
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Human label for the period containing `isoDate` — `Jul 2026`, `Q3 2026`,
 * `H2 2026`, `2026`. This is the string that appears on an over-budget
 * warning, so it has to be the shape a person would say out loud.
 */
export function budgetPeriodLabel(resetPeriod: BudgetResetPeriod, isoDate: string): string {
  // Validates both inputs, so the switch below is genuinely exhaustive.
  const { start } = budgetPeriodWindow(resetPeriod, isoDate);
  const year = Number(start.slice(0, 4));
  const startMonth = Number(start.slice(5, 7));
  switch (resetPeriod) {
    case 'monthly':
      return `${MONTH_NAMES[startMonth - 1]} ${year}`;
    case 'quarterly':
      return `Q${Math.floor((startMonth - 1) / 3) + 1} ${year}`;
    case 'semi_annual':
      return `H${Math.floor((startMonth - 1) / 6) + 1} ${year}`;
    case 'annual':
      return String(year);
  }
}

/**
 * Maximum hours a single budget row may carry. A budget is a period allowance,
 * not a lifetime one — 100k hours is ~50 person-years and is certainly a typo.
 * Shared by the server validator and the web form so the cap can't drift.
 */
export const MAX_BUDGET_HOURS = 100_000;

/**
 * A budget's per-role hour allocations: `{ "<service_role_id>": hours }`.
 *
 * Keyed by service-role **id**, never by name — a role rename must not orphan
 * every budget that mentions it. Stored as a jsonb map on `customer_budgets`
 * rather than a child table because the row is the unit of edit: a budget is
 * replaced wholesale, never line-by-line, so normalized lines would be deleted
 * and recreated on every save.
 *
 * A role with no allocation is ABSENT from the map. There is no zero entry —
 * absence means "unbudgeted", which is different from "budgeted at zero", and
 * collapsing the two would make an unbudgeted role look over budget the moment
 * anyone logged an hour against it.
 */
export type BudgetAllocations = Record<string, number>;

/**
 * Canonical form for storage and comparison: keys sorted, values pinned to the
 * 2-decimal scale, non-positive entries dropped.
 *
 * Sorting matters because the audit diff compares serialized objects, so key
 * order alone would otherwise register as a change.
 */
export function normalizeAllocations(input: BudgetAllocations): BudgetAllocations {
  const out: BudgetAllocations = {};
  for (const key of Object.keys(input).sort()) {
    const value = input[key];
    if (value === undefined || !Number.isFinite(value) || value <= 0) continue;
    out[key] = Number(value.toFixed(2));
  }
  return out;
}

/**
 * True when two allocation maps mean the same thing.
 *
 * This is what decides whether an imported row is a no-op, so it has to be
 * insensitive to everything that is representation rather than meaning:
 * key order (JSON object order carries no meaning) and numeric scale (`40`
 * from a spreadsheet cell vs `40.00` round-tripped through jsonb). A plain
 * `JSON.stringify` comparison gets both wrong, and the cost is not cosmetic —
 * every re-import would truncate the live budget and insert a duplicate.
 */
export function allocationsEqual(a: BudgetAllocations, b: BudgetAllocations): boolean {
  const na = normalizeAllocations(a);
  const nb = normalizeAllocations(b);
  const keys = Object.keys(na);
  if (keys.length !== Object.keys(nb).length) return false;
  return keys.every((k) => nb[k] === na[k]);
}

/** Total budgeted hours across every role in one reset period. */
export function totalAllocatedHours(a: BudgetAllocations): number {
  return Number(
    Object.values(a)
      .reduce((sum, h) => sum + h, 0)
      .toFixed(2),
  );
}

/**
 * How many recent period occurrences the Budgets tab reports actuals for.
 * Shared so the server's default and the client's request can't drift.
 */
export const BUDGET_ACTUALS_PERIODS = 3;

/**
 * The `count` most recent period occurrences ending at or containing
 * `anchorDate`, newest first, restricted to those that overlap
 * `[notBefore, notAfter)` when either bound is given.
 *
 * "Most recent" starts with the period CONTAINING the anchor — for a live
 * budget that is the period in progress, which is the one a reader cares
 * about most — and walks backwards a whole span at a time. Walking is on the
 * period START (always the first of a month), so stepping back is plain month
 * arithmetic and never has to reason about month lengths.
 *
 * Fewer than `count` windows come back when the range runs out, which is the
 * normal case for a budget that started recently: the caller shows what
 * exists rather than padding with periods the budget never covered.
 *
 * `notBefore` is inclusive and `notAfter` exclusive, matching a budget's
 * `[effectiveFrom, effectiveTo)`. A window survives when it OVERLAPS that
 * range, not when it is contained by it — a budget starting mid-period still
 * governs the rest of that period (docs/budgets.md: nothing is prorated).
 */
export function recentBudgetPeriodWindows(
  resetPeriod: BudgetResetPeriod,
  anchorDate: string,
  count: number,
  bounds: { notBefore?: string; notAfter?: string | null } = {},
): Array<{ start: string; end: string }> {
  const span = BUDGET_RESET_PERIOD_MONTHS[resetPeriod];
  // Validates both inputs; also the only place an unknown period is caught.
  let window = budgetPeriodWindow(resetPeriod, anchorDate);

  // Wind the anchor back to the last window the range actually admits. This
  // is NOT folded into the loop below: a window past `notAfter` must not
  // consume one of the `count` slots, or a caller anchored on today would get
  // one period back for a budget that ended last quarter. Terminates because
  // the start strictly decreases towards a fixed date.
  while (bounds.notAfter != null && window.start >= bounds.notAfter) {
    window = shiftPeriodStart(window.start, -span, span);
  }

  const out: Array<{ start: string; end: string }> = [];
  for (let i = 0; i < count; i++) {
    // Half-open on both sides: a window overlaps the range while its END is
    // still after `notBefore`. Once it isn't, every earlier one misses too.
    if (bounds.notBefore != null && window.end <= bounds.notBefore) break;
    out.push(window);
    window = shiftPeriodStart(window.start, -span, span);
  }
  return out;
}

/** Moves a period start `months` months (may be negative) and returns the
 *  whole window it opens. Arithmetic on a 0-based absolute month index, so a
 *  year boundary needs no special case. */
function shiftPeriodStart(
  start: string,
  months: number,
  span: number,
): { start: string; end: string } {
  const index = Number(start.slice(0, 4)) * 12 + (Number(start.slice(5, 7)) - 1) + months;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  const endIndex = index + span;
  return {
    start: `${y}-${pad2(m)}-01`,
    end: `${Math.floor(endIndex / 12)}-${pad2((endIndex % 12) + 1)}-01`,
  };
}
